import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Clock, Users, MapPin, Calendar, Star, Shield, Check, ExternalLink, Image, StickyNote } from 'lucide-react';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';

export default function RequestDetailPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { api, token } = useAuth();
  const { t, language } = useLanguage();

  const [request, setRequest] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(0);
  const [selectingOffer, setSelectingOffer] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [reqRes, offersRes] = await Promise.all([
        api().get(`/requests/${requestId}`),
        api().get(`/requests/${requestId}/offers`)
      ]);
      setRequest(reqRes.data);
      setOffers(offersRes.data);
    } catch (error) {
      toast.error(language === 'en' ? 'Error loading request' : 'Error cargando solicitud');
    } finally {
      setLoading(false);
    }
  }, [requestId, api, language]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (request?.expires_at) {
      const updateTimer = () => {
        const expiresAt = new Date(request.expires_at).getTime();
        const now = Date.now();
        const diff = Math.max(0, Math.floor((expiresAt - now) / 1000));
        setTimeLeft(diff);
      };
      updateTimer();
      const timer = setInterval(updateTimer, 1000);
      return () => clearInterval(timer);
    }
  }, [request?.expires_at]);

  const formatTime = (seconds) => {
    if (seconds <= 0) return '00:00:00';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffMins < 1) return language === 'en' ? 'Just now' : 'Hace un momento';
    if (diffMins < 60) return language === 'en' ? `${diffMins} min ago` : `Hace ${diffMins} min`;
    if (diffHours < 24) return language === 'en' ? `${diffHours}h ago` : `Hace ${diffHours}h`;
    return date.toLocaleDateString();
  };

  const handleSelectOffer = async (offerId) => {
    setSelectingOffer(offerId);
    try {
      const response = await api().post('/bookings', {
        offer_id: offerId,
        payment_type: 'full'
      });
      toast.success(language === 'en' ? 'Booking created!' : '¡Reserva creada!');
      navigate(`/dashboard/bookings/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? 'Error' : 'Error'));
    } finally {
      setSelectingOffer(null);
    }
  };

  const getPhotoUrl = (path) => {
    if (!path) return null;
    // Base64 dataURLs are returned as-is
    if (typeof path === 'string' && path.startsWith('data:')) return path;
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-caribbean"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{language === 'en' ? 'Request not found' : 'Solicitud no encontrada'}</p>
      </div>
    );
  }

  const getCategoryName = (id) => {
    const names = {
      yacht: language === 'en' ? 'Yacht' : 'Yate',
      tour: 'Tour',
      activity: language === 'en' ? 'Activity' : 'Actividad',
      party: language === 'en' ? 'Party' : 'Fiesta',
      car_rental: language === 'en' ? 'Car Rental' : 'Renta de Auto',
      lodging: language === 'en' ? 'Lodging' : 'Hospedaje'
    };
    return names[id] || id;
  };

  const getLocationName = (id) => {
    const names = {
      playa_del_carmen: 'Playa del Carmen',
      cancun: 'Cancún',
      tulum: 'Tulum',
      quintana_roo: 'Quintana Roo',
      los_cabos: 'Los Cabos'
    };
    return names[id] || id;
  };

  return (
    <MobileShell>
      <TopBar
        showBrand={false}
        title={language === 'en' ? 'Request' : 'Solicitud'}
        right={
          <button onClick={() => navigate('/dashboard')} className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted">
            <ArrowLeft size={18} />
          </button>
        }
      />
      <div className="px-4 py-4" data-testid="request-detail-page">
        <div className="space-y-4">

        {/* Request Summary */}
        <div className="bg-card rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="font-heading text-2xl font-bold">{getCategoryName(request.category)}</h1>
              <div className="flex items-center gap-2 text-muted-foreground mt-1">
                <MapPin size={16} />
                <span>{getLocationName(request.location)}</span>
              </div>
            </div>
            <Badge
              variant={request.status === 'active' ? 'default' : 'secondary'}
              className={request.status === 'active' ? 'bg-caribbean' : ''}
            >
              {request.status === 'active' && (language === 'en' ? 'Active' : 'Activo')}
              {request.status === 'booked' && (language === 'en' ? 'Booked' : 'Reservado')}
              {request.status === 'expired' && (language === 'en' ? 'Expired' : 'Expirado')}
            </Badge>
          </div>

          <div className="grid grid-cols-3 gap-4 py-4 border-y">
            <div className="text-center">
              <Calendar className="mx-auto text-muted-foreground mb-1" size={20} />
              <p className="text-sm font-medium">{request.date}</p>
              <p className="text-xs text-muted-foreground">{request.time}</p>
            </div>
            <div className="text-center">
              <Users className="mx-auto text-muted-foreground mb-1" size={20} />
              <p className="text-sm font-medium">{request.people_count}</p>
              <p className="text-xs text-muted-foreground">{language === 'en' ? 'People' : 'Personas'}</p>
            </div>
            <div className="text-center">
              <span className="block font-bold text-caribbean">${request.budget_min} - ${request.budget_max}</span>
              <p className="text-xs text-muted-foreground">USD</p>
            </div>
          </div>

          {request.notes && (
            <p className="mt-4 text-sm text-muted-foreground">{request.notes}</p>
          )}
        </div>

        {/* Countdown Timer */}
        {request.status === 'active' && (
          <div className={`bg-card rounded-2xl shadow-xl p-6 mb-6 text-center ${timeLeft < 1800 ? 'animate-countdown-pulse' : ''}`}>
            <div className="flex items-center justify-center gap-2 text-muted-foreground mb-2">
              <Clock size={20} />
              <span>{language === 'en' ? 'Time Remaining to Receive Offers' : 'Tiempo Restante para Recibir Ofertas'}</span>
            </div>
            <p className={`font-heading text-4xl font-bold ${timeLeft < 1800 ? 'text-rosa' : 'text-foreground'}`}>
              {formatTime(timeLeft)}
            </p>
            {timeLeft <= 0 && offers.length > 0 && (
              <div className="mt-4 p-4 bg-gold/20 rounded-xl">
                <p className="text-lg font-semibold text-gold">
                  {language === 'en' ? '⏰ Time is up! Choose one of the offers below' : '⏰ ¡El tiempo terminó! Escoge una de las ofertas abajo'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Offers */}
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-bold">
            {language === 'en' ? 'Offers' : 'Ofertas'} ({offers.length})
          </h2>

          {offers.length === 0 ? (
            <div className="bg-card rounded-2xl shadow-xl p-8 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="text-muted-foreground" size={28} />
              </div>
              <p className="text-muted-foreground">
                {language === 'en' 
                  ? 'No offers yet. Providers are reviewing your request.'
                  : 'Sin ofertas aún. Los proveedores están revisando tu solicitud.'}
              </p>
            </div>
          ) : (
            offers.map((offer) => (
              <div
                key={offer.id}
                data-testid={`offer-${offer.id}`}
                className="bg-card rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <Link 
                    to={`/provider/${offer.provider_id}`}
                    className="flex items-center gap-3 group"
                  >
                    <div className="w-12 h-12 bg-caribbean/10 rounded-full flex items-center justify-center overflow-hidden">
                      {offer.provider_photo ? (
                        <img src={getPhotoUrl(offer.provider_photo)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="font-heading font-bold text-caribbean">
                          {offer.provider_name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold group-hover:text-caribbean transition-colors">{offer.provider_name}</p>
                        {offer.provider_is_verified && (
                          <Shield className="text-caribbean" size={16} />
                        )}
                        <ExternalLink size={14} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      {offer.provider_company && (
                        <p className="text-sm text-muted-foreground">{offer.provider_company}</p>
                      )}
                      {offer.provider_rating > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="text-gold fill-gold" size={14} />
                          <span className="text-sm">{offer.provider_rating.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                  </Link>
                  <div className="text-right">
                    <p className="font-heading text-2xl font-bold text-caribbean">${offer.price}</p>
                    <p className="text-xs text-muted-foreground">USD</p>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center justify-end gap-1">
                      <Clock size={12} />
                      {formatTimeAgo(offer.created_at)}
                    </p>
                  </div>
                </div>

                <p className="text-sm mb-4">{offer.description}</p>

                {/* Offer Images */}
                {offer.images && offer.images.length > 0 && (
                  <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                    {offer.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={getPhotoUrl(img)}
                        alt={`Offer ${idx + 1}`}
                        className="w-24 h-24 rounded-xl object-cover cursor-pointer hover:opacity-90 transition-opacity flex-shrink-0"
                        onClick={() => setSelectedImage(getPhotoUrl(img))}
                      />
                    ))}
                  </div>
                )}

                {offer.includes && offer.includes.length > 0 && (
                  <div className="mb-4 rounded-2xl border border-caribbean/20 bg-caribbean/5 p-3">
                    <p className="text-[10px] uppercase tracking-wider text-caribbean font-bold mb-2 flex items-center gap-1">
                      <Check size={12} />
                      {language === 'en' ? "What's included" : 'Qué incluye'}
                    </p>
                    <ul className="space-y-1.5">
                      {offer.includes.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="mt-0.5 w-4 h-4 rounded-full bg-caribbean/15 flex items-center justify-center shrink-0">
                            <Check size={10} className="text-caribbean" />
                          </span>
                          <span className="leading-snug">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {offer.note && (
                  <div className="mb-4 rounded-2xl border border-gold/30 bg-gold/10 p-3">
                    <p className="text-[10px] uppercase tracking-wider text-gold-foreground font-bold mb-1 flex items-center gap-1">
                      <StickyNote size={12} className="text-gold" />
                      {language === 'en' ? 'Note from provider' : 'Nota del proveedor'}
                    </p>
                    <p className="text-sm leading-snug whitespace-pre-wrap">{offer.note}</p>
                  </div>
                )}

                {request.status === 'active' && (
                  <Button
                    data-testid={`select-offer-${offer.id}`}
                    onClick={() => handleSelectOffer(offer.id)}
                    disabled={selectingOffer === offer.id}
                    className="w-full rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold"
                  >
                    {selectingOffer === offer.id 
                      ? (language === 'en' ? 'Selecting...' : 'Seleccionando...')
                      : (language === 'en' ? 'Select This Offer' : 'Seleccionar Esta Oferta')}
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Image Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <img 
            src={selectedImage} 
            alt="Offer" 
            className="max-w-full max-h-full rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <ArrowLeft size={32} />
          </button>
        </div>
      )}
      </div>
    </MobileShell>
  );
}
